/**
 * TextSizeControl
 *
 * Compact control for adjusting the app's base font size preference.
 * Persisted via useTheme — applies a data-text-size attribute on <html>
 * which scales all rem-based text proportionally.
 *
 * Usage:
 *   <TextSizeControl />                    // compact icon row (default)
 *   <TextSizeControl variant="full" />     // labeled buttons (settings page)
 */
import React from 'react';
import { useTheme } from '../../hooks/useTheme';

const SIZE_CONFIG = {
  compact: { label: 'Compact', shortLabel: 'A', scale: 0.85, desc: '14px base' },
  normal:  { label: 'Normal',  shortLabel: 'A', scale: 1.0,  desc: '16px base (default)' },
  large:   { label: 'Large',   shortLabel: 'A', scale: 1.12, desc: '18px base' },
  xl:      { label: 'X-Large', shortLabel: 'A', scale: 1.25, desc: '20px base' },
};

export function TextSizeControl({ variant = 'compact', className = '' }) {
  const { textSize, setTextSize } = useTheme();

  if (variant === 'full') {
    return (
      <div className={`flex flex-col gap-2 ${className}`}>
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Text size</p>
        <div className="flex gap-2">
          {Object.entries(SIZE_CONFIG).map(([key, config]) => {
            const isActive = textSize === key;
            return (
              <button
                key={key}
                onClick={() => setTextSize(key)}
                title={config.desc}
                aria-pressed={isActive}
                className={`flex-1 flex flex-col items-center gap-1 py-2.5 rounded-xl border text-xs transition-all
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/50
                  ${isActive
                    ? 'border-amber-400/30 bg-amber-400/[0.08] text-amber-400'
                    : 'border-white/[0.08] bg-transparent text-gray-500 hover:border-white/[0.16] hover:text-gray-300'
                  }`}
              >
                <span
                  className="font-semibold leading-none"
                  style={{ fontSize: `${12 * config.scale}px` }}
                >
                  {config.shortLabel}
                </span>
                <span className="text-[10px] leading-none">{config.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // compact: single row of A buttons
  return (
    <div
      className={`flex items-center gap-0.5 p-1 rounded-lg glass ${className}`}
      title="Text size"
      role="group"
      aria-label="Text size"
    >
      {Object.entries(SIZE_CONFIG).map(([key, config]) => {
        const isActive = textSize === key;
        return (
          <button
            key={key}
            onClick={() => setTextSize(key)}
            title={`${config.label} — ${config.desc}`}
            aria-pressed={isActive}
            aria-label={`${config.label} text`}
            className={`px-1.5 py-1 rounded-md transition-all
              focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-amber-400/50
              ${isActive
                ? 'bg-amber-400/[0.12] text-amber-400'
                : 'text-gray-600 hover:text-gray-300'
              }`}
          >
            <span
              className="font-semibold leading-none"
              style={{ fontSize: `${10 * config.scale}px` }}
            >
              A
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default TextSizeControl;
